/**
 * CR-7320: ERP fault-code → fault category mapping, beside the fault taxonomy
 * in Admin Setup > Quality. One mapping per organisation (the taxonomy's
 * owner — the company group for a grouped customer).
 *
 * Add / edit / delete rows, import a CSV (dry-run preview first), sign the
 * mapping off ("Confirm mapping" — clears provisional, records who and when),
 * and "Apply to NCRs" (blank categories only unless re-apply is ticked).
 * Customer admins and Helix only; the API refuses everyone else.
 */
import { useCallback, useEffect, useState } from 'react';
import { qualitySettingsAPI } from '../../../lib/api';
import type { FaultCodeMapping, FaultCodeMappingContext } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertCircle, CheckCircle2, Loader2, Play, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import FaultCodeMappingImportDialog from './FaultCodeMappingImportDialog';

interface FaultCodeMappingSectionProps {
  organisation?: string;
}

function CategorySelect({ value, context, onChange, id }: {
  value: number | ''; context: FaultCodeMappingContext; onChange: (id: number) => void; id: string;
}) {
  const groups = Array.from(new Set(context.categories.map((c) => c.fault_group_name)));
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
    >
      <option value="" disabled>Choose a category…</option>
      {groups.map((g) => (
        <optgroup key={g} label={g}>
          {context.categories.filter((c) => c.fault_group_name === g).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

export default function FaultCodeMappingSection({ organisation }: FaultCodeMappingSectionProps) {
  const [rows, setRows] = useState<FaultCodeMapping[]>([]);
  const [context, setContext] = useState<FaultCodeMappingContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [force, setForce] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newCategory, setNewCategory] = useState<number | ''>('');
  // Inline two-step confirmation for the sign-off and deletes (no native dialogs).
  const [pending, setPending] = useState<{ kind: 'confirm' } | { kind: 'delete'; row: FaultCodeMapping } | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [list, ctx] = await Promise.all([
        qualitySettingsAPI.getFaultCodeMappings(organisation),
        qualitySettingsAPI.getFaultCodeMappingContext(organisation),
      ]);
      setRows(list.data);
      setContext(ctx.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 403 ? 'Only an administrator can manage the ERP fault-code mapping.'
        : 'Failed to load the ERP fault-code mapping.');
    } finally {
      setLoading(false);
    }
  }, [organisation]);

  useEffect(() => { load(); }, [load]);

  const run = async (key: string, fn: () => Promise<void>) => {
    try {
      setBusy(key);
      await fn();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      const first = data ? Object.values(data)[0] : undefined;
      toast.error(Array.isArray(first) ? String(first[0]) : typeof first === 'string' ? first : 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  const changeCategory = (row: FaultCodeMapping, categoryId: number) => run(`row-${row.id}`, async () => {
    const resp = await qualitySettingsAPI.updateFaultCodeMapping(row.id, { fault_category: categoryId }, organisation);
    setRows((prev) => prev.map((r) => (r.id === row.id ? resp.data : r)));
    toast.success(`${row.erp_code} now maps to ${resp.data.fault_category_name} (provisional) — apply to NCRs to update them`);
    await load();
  });

  const remove = (row: FaultCodeMapping) => run(`del-${row.id}`, async () => {
    await qualitySettingsAPI.deleteFaultCodeMapping(row.id, organisation);
    toast.success(`Removed ${row.erp_code}`);
    await load();
  });

  const add = () => run('add', async () => {
    if (!newCode.trim() || newCategory === '') return;
    await qualitySettingsAPI.createFaultCodeMapping({ erp_code: newCode.trim(), fault_category: newCategory }, organisation);
    toast.success(`Added ${newCode.trim().toUpperCase()}`);
    setNewCode('');
    setNewCategory('');
    await load();
  });

  const confirmAll = (provisional: boolean) => run('confirm', async () => {
    const resp = await qualitySettingsAPI.confirmFaultCodeMappings(provisional, organisation);
    toast.success(provisional ? `${resp.data.changed} rows re-opened as provisional`
      : `Mapping confirmed: ${resp.data.changed} rows signed off`);
    await load();
  });

  const apply = () => run('apply', async () => {
    const resp = await qualitySettingsAPI.applyFaultCodeMappings(force, organisation);
    if (resp.data.queued) {
      toast.success('Queued: the NCRs are being re-classified in the background');
    } else {
      toast.success(`Applied: ${resp.data.changed ?? 0} NCRs updated`);
    }
  });

  return (
    <Card data-testid="fault-code-mapping-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">ERP Fault Code Mapping</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {!loading && error && <div className="flex items-center gap-2 text-sm text-destructive"><AlertCircle className="h-4 w-4" /> {error}</div>}
        {!loading && context && (
          <>
            <p className="text-sm text-muted-foreground">
              Each ERP fault code on an imported NCR (e.g. <code>MC05:</code>) sets the NCR&apos;s Fault Group and
              Category from this list — {context.owner_name}. {context.provisional_rows} of {context.total_rows} rows
              are provisional (awaiting sign-off).
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="h-4 w-4 mr-1" /> Import CSV
              </Button>
              <Button size="sm" variant="outline" disabled={busy !== null || context.provisional_rows === 0}
                onClick={() => setPending({ kind: 'confirm' })}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm mapping
              </Button>
              <Button size="sm" variant="ghost" disabled={busy !== null || context.provisional_rows === context.total_rows}
                onClick={() => confirmAll(true)}>
                Re-open as provisional
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-xs">
                  <Checkbox checked={force} onCheckedChange={(v) => setForce(v === true)} aria-label="Re-apply over categories already set" />
                  Re-apply over categories already set
                </label>
                <Button size="sm" onClick={apply} disabled={busy !== null || context.total_rows === 0}>
                  {busy === 'apply' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                  Apply to NCRs
                </Button>
              </div>
            </div>
            {pending && (
              <div className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm" data-testid="fault-mapping-pending">
                <span>
                  {pending.kind === 'confirm'
                    ? `Sign off all ${context.provisional_rows} provisional rows? They are recorded as confirmed by you, now.`
                    : `Delete the mapping for ${pending.row.erp_code}? NCRs already classified keep their category.`}
                </span>
                <Button size="sm" className="h-7" onClick={() => {
                  const p = pending; setPending(null);
                  if (p.kind === 'confirm') confirmAll(false); else remove(p.row);
                }}>{pending.kind === 'confirm' ? 'Yes, confirm mapping' : 'Yes, delete'}</Button>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setPending(null)}>Cancel</Button>
              </div>
            )}
            <div className="max-h-[480px] overflow-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[90px]">ERP code</TableHead>
                    <TableHead>ERP text</TableHead>
                    <TableHead className="w-[320px]">Fault group → category</TableHead>
                    <TableHead className="w-[70px] text-right">NCRs</TableHead>
                    <TableHead className="w-[150px]">Status</TableHead>
                    <TableHead className="w-[40px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} data-testid={`fault-mapping-${row.erp_code}`}>
                      <TableCell className="font-mono text-xs">{row.erp_code}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{row.erp_description || '—'}</TableCell>
                      <TableCell>
                        <div className="text-[11px] text-muted-foreground">{row.fault_group_name}</div>
                        <CategorySelect id={`fault-mapping-category-${row.erp_code}`} value={row.fault_category}
                          context={context} onChange={(id) => changeCategory(row, id)} />
                      </TableCell>
                      <TableCell className="text-right text-xs">{row.ncr_count}</TableCell>
                      <TableCell className="text-xs">
                        {row.is_provisional
                          ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">Provisional</span>
                          : <span className="text-emerald-700" title={row.confirmed_at ?? ''}>Confirmed{row.confirmed_by_name ? ` by ${row.confirmed_by_name}` : ''}</span>}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                          aria-label={`Delete ${row.erp_code}`} disabled={busy !== null}
                          onClick={() => setPending({ kind: 'delete', row })}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-sm text-muted-foreground">
                        No ERP codes mapped yet. Add one below or import the CSV.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="flex gap-2 pt-1">
              <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="ERP code, e.g. MC05"
                className="w-[160px] h-8 text-sm" aria-label="New ERP code" />
              <div className="w-[320px]">
                <CategorySelect id="fault-mapping-new-category" value={newCategory} context={context} onChange={setNewCategory} />
              </div>
              <Button size="sm" className="h-8" onClick={add} disabled={busy !== null || !newCode.trim() || newCategory === ''}>
                {busy === 'add' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />} Add
              </Button>
            </div>
          </>
        )}
        <FaultCodeMappingImportDialog open={importOpen} organisation={organisation}
          onClose={() => setImportOpen(false)} onSaved={() => { setImportOpen(false); load(); }} />
      </CardContent>
    </Card>
  );
}
